import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";
import { isBooleanTrue, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { createWithId, getAll, getById, remove, update, updateField } from "../utils/dbUtils";
import { db } from "../firebase";
import { asTimeZone } from "../utils/dateFormatter";
import moment from "moment-timezone";

class CommercialEquivalencyGroup {
    static collectionName = 'commercialEquivalencyGroups';

    id = null;
    name = null;
    description = null;
    enabled = false;
    variantMatchStrategy = 'NONE';

    constructor(group) {
        if (isObjectNotEmpty(group)) {
            if (isStringNotEmpty(group.id)) { this.id = group.id; }
            if (isStringNotEmpty(group.name)) { this.name = group.name; }
            if (isStringNotEmpty(group.description)) { this.description = group.description; }
            if (isBooleanTrue(group.enabled)) { this.enabled = true; }
            if (isStringNotEmpty(group.variantMatchStrategy)) { this.variantMatchStrategy = group.variantMatchStrategy; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            description: this.description,
            enabled: this.enabled,
            variantMatchStrategy: this.variantMatchStrategy,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.id)) {
            throw new Error('Group code is required.');
        }
        if (isStringEmpty(this.name)) {
            throw new Error('Name is required.');
        }
        if (isStringEmpty(this.variantMatchStrategy)) {
            throw new Error('Variant match strategy is required.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await createWithId(CommercialEquivalencyGroup.collectionName, this.id, this.toDto());
        }

        return null;
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(CommercialEquivalencyGroup.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedGroup, oldObj) => {
        const updatedObj = new CommercialEquivalencyGroup(updatedGroup);
        if (updatedObj.isValid() && id === updatedGroup.id) {
            const success = await update(CommercialEquivalencyGroup.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj.toDto() };
            }
        } else {
            throw new Error('id and id field of the updated commercial equivalency group must match.');
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(CommercialEquivalencyGroup.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(CommercialEquivalencyGroup.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(CommercialEquivalencyGroup.collectionName);
    }

    static getCount = async (queryParameters = {}) => {
        const { start, end } = queryParameters;
        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            const startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            const endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        const q = query(collection(db, CommercialEquivalencyGroup.collectionName), ...queryConstraints);
        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters = {}, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { start, end } = queryParameters;
        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            const startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            const endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, CommercialEquivalencyGroup.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, CommercialEquivalencyGroup.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, CommercialEquivalencyGroup.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        const snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }
}

export default CommercialEquivalencyGroup;
